import { useEffect } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BusinessDoc } from '../../shared/api/converters.js';
import {
  cancelMinuteTopup,
  checkIncludedMinutes,
  prepareActivation,
  completeBillingPaymentSetup,
  createBillingPaymentSetup,
  createMinuteTopup,
  getBillingOverview,
  listBillingHistory,
  purchaseRequestId,
  quoteMinuteTopup,
  saveAutoTopupSettings,
  saveBillingSmsSettings,
  syncMinuteTopup,
} from '../../shared/api/billing.js';
import { keys } from '../../shared/api/keys.js';
import type { GetBillingOverviewResponse, SaveAutoTopupSettingsRequest, SaveBillingSmsSettingsRequest } from '@acs/shared';

/**
 * Prepaid billing hooks (doc 42 W6). Data access → these hooks → UI; none of
 * them writes a financial field directly.
 *
 * The overview is a callable, not a listener. The BALANCE is not taken from
 * it: `business.billingSummary` is a live listener and the balance card reads
 * that directly, so a call ticking the wallet every 15 s re-renders without a
 * round trip. The overview (prices, card, spend, active order) is refetched
 * only when something it shows changes — payment state, the open order, the
 * auto top-up revision, the subscription — never on every checkpoint. Nothing
 * here paints an optimistic balance (§4).
 */
export function useBillingOverview(business: BusinessDoc) {
  const qc = useQueryClient();
  const enabled = business.billingSummary.mode === 'prepaid_v1';
  const summary = business.billingSummary;
  // The allowance fields too: a grant, a scheduled period or a revocation
  // changes the overview's included-minutes state (doc 61 R04).
  const stamp = `${summary.paymentState}|${summary.activeTopupOrderId ?? ''}|${business.billingPreferences.autoTopup.revision}|${business.subscription.status}|${business.subscription.kind ?? ''}|${business.billingPreferences.paymentMethod?.last4 ?? ''}|${summary.allowanceStatus ?? ''}|${summary.allowancePeriodStart?.getTime() ?? ''}|${summary.pendingAllowanceStart?.getTime() ?? ''}`;
  useEffect(() => {
    if (!enabled) return;
    void qc.invalidateQueries({ queryKey: keys.billingOverview(business.id) });
    void qc.invalidateQueries({ queryKey: keys.billingHistory(business.id) });
  }, [stamp, enabled, business.id, qc]);
  return useQuery({
    queryKey: keys.billingOverview(business.id),
    queryFn: () => getBillingOverview({ businessId: business.id }),
    enabled,
    staleTime: 30_000,
  });
}

/** "Check again" while included minutes are being prepared (doc 61 R04):
 *  the server re-reads the latest subscription invoice (at most once a
 *  minute), then the overview is refetched for the verdict. */
export function useCheckIncludedMinutes(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => checkIncludedMinutes({ businessId }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.billingOverview(businessId) }),
  });
}

/** The Activate step opened (doc 63): the server puts the business on the
 *  included-minutes offer if it is eligible. The business document's listener
 *  carries the result; the overview is refetched for the new prices/state. */
export function usePrepareActivation(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => prepareActivation({ businessId }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.billingOverview(businessId) }),
  });
}

export function useQuoteTopup(businessId: string) {
  return useMutation({ mutationFn: (packageId: string) => quoteMinuteTopup({ businessId, packageId }) });
}

/**
 * The exact, tax-included price of ONE package for this business, cached per
 * package for most of the quote's life. A quote is a paid Stripe Tax
 * calculation (doc 57), so the plan screen asks once per package it actually
 * shows — never per render, never per keystroke — and the caller debounces
 * selection changes before passing `packageId`.
 */
export function usePackageQuote(businessId: string, packageId: string | null, enabled: boolean) {
  return useQuery({
    // Its own key, NOT under the overview: overview invalidations happen on
    // every payment-state change and must not buy a fresh tax calculation.
    queryKey: keys.billingQuote(businessId, packageId),
    queryFn: () => quoteMinuteTopup({ businessId, packageId: packageId! }),
    enabled: enabled && !!packageId,
    // The server keeps a quote 10 minutes; refresh well before it lapses.
    staleTime: 8 * 60_000,
    gcTime: 8 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** Create the order. The UI then offers Stripe's hosted invoice page as a
 *  LINK the owner clicks (a tab opened after an await is popup-blocked), and
 *  waits for the server to confirm payment — never an optimistic balance (§4).
 *  The hosted page has no way back to us, so this page stays open. */
export function useBuyTopup(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quoteId: string) =>
      createMinuteTopup({
        businessId,
        quoteId,
        requestId: purchaseRequestId(quoteId),
        returnOrigin: window.location.origin,
      }),
    // A quote names ONE purchase (it is the dedupe key): once an order exists
    // for it, buying again with the same quote would return that order, paid
    // or cancelled. Drop every cached quote so the next purchase gets a new one.
    onSuccess: () => qc.removeQueries({ queryKey: ['billing', businessId, 'quote'] }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.billingOverview(businessId) }),
  });
}

export function useSyncTopup(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => syncMinuteTopup({ businessId, orderId }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.billingOverview(businessId) }),
  });
}

export function useCancelTopup(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => cancelMinuteTopup({ businessId, orderId }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.billingOverview(businessId) }),
  });
}

/** Stripe-hosted card setup; full-page navigation, like Checkout. */
export function useStartCardSetup(businessId: string) {
  return useMutation({
    mutationFn: async () => {
      const { url } = await createBillingPaymentSetup({ businessId, returnOrigin: window.location.origin });
      window.location.assign(url);
    },
  });
}

export function useCompleteCardSetup(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => completeBillingPaymentSetup({ businessId, sessionId }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.billingOverview(businessId) }),
  });
}

export function useSaveAutoTopup(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<SaveAutoTopupSettingsRequest, 'businessId'>) => saveAutoTopupSettings({ businessId, ...input }),
    // What was just saved is what the server now holds: put it in the cached
    // overview at once, so a screen opened before the refetch lands (Back in
    // the wizard) shows the owner's choice, not the settings it replaced.
    onSuccess: (r, input) =>
      qc.setQueryData<GetBillingOverviewResponse>(keys.billingOverview(businessId), (old) =>
        old && {
          ...old,
          autoTopup: {
            enabled: input.enabled,
            packageId: input.packageId,
            triggerSeconds: input.triggerSeconds,
            monthlyGrossCapMinor: input.monthlyGrossCapMinor,
            maxGrossPerPurchaseMinor: input.enabled ? input.acceptedGrossPerPurchaseMinor : old.autoTopup.maxGrossPerPurchaseMinor,
            revision: r.revision,
          },
        },
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.billingOverview(businessId) }),
  });
}

export function useSaveBillingSms(businessId: string) {
  return useMutation({
    mutationFn: (input: Omit<SaveBillingSmsSettingsRequest, 'businessId'>) => saveBillingSmsSettings({ businessId, ...input }),
  });
}

export function useBillingHistory(businessId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: keys.billingHistory(businessId),
    queryFn: ({ pageParam }) => listBillingHistory({ businessId, cursor: pageParam, limit: 10 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}
